"use client";

import { useMemo } from "react";

import { DynamicDocumentInstructions, WorkflowAction } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { useDocumentManagementInstructions } from "@/src/lib/data-fetchers/document-management-instructions-fetchers";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { isPassthroughAssignee } from "@/src/lib/ehr/customer/heartplace/passthrough-assignee";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { renderTemplate } from "@/src/lib/templating";
import type { StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";

export interface InstructionField {
  label: string;
  value: string;
}

export interface ProcessedInstructions {
  displayName: string;
  standardFields: InstructionField[];
  extraFields: InstructionField[];
  instructions: readonly string[] | null;
  note: string | null;
  noteLabel: string | null;
  statusLine: string | null;
  workflowAction: WorkflowAction | null;
  additionalWorkflow: readonly string[] | null;
  faxRecipientNumber: string | null;
  tags: readonly string[] | null;
  stepOutputs: StepOutputs | null;
  ccDescription: string | null;
  processInstructionText: (text: string) => string;
}

interface LocationAssignment {
  authLocation?: string | null;
  staffLocation?: string | null;
  locationPrefix?: string | null;
  faxRecipientNumber?: string | null;
  resolvedAssignment?: string | null;
}

function getGroupExtraFields(groupName: string, task: Task, section: Section): InstructionField[] {
  switch (groupName) {
    case ConfigSubdomain.STRIDECARE:
      return [
        {
          label: "Patient Name",
          value: [task.patient?.familyName, task.patient?.givenName].filter(Boolean).join(", ") || "N/A",
        },
        { label: "EHR ID", value: (task.patient && tryGetEhrSystemId(task.patient)) || "N/A" },
        { label: "Document ID", value: section.ehrDocumentId || "N/A" },
      ];
    default:
      return [];
  }
}

function replaceLocationPlaceholder(text: string | null, locationValue: string | null): string {
  if (!text) {
    return locationValue || "";
  }

  if (!locationValue) {
    return text;
  }

  return text.replace(/\{\{?location\}\}?/g, locationValue);
}

export function useBuildInstructions(
  task: Task,
  section: Section,
  mapping: DynamicDocumentInstructions | null | undefined,
  stepOutputs: StepOutputs | null | undefined,
  ccDescription: string | null | undefined,
  location: LocationAssignment = {}
): ProcessedInstructions | null {
  const {
    authLocation = null,
    staffLocation = null,
    locationPrefix = null,
    faxRecipientNumber = null,
    resolvedAssignment = null,
  } = location;

  return useMemo<ProcessedInstructions | null>(() => {
    if (!mapping) return null;

    const reviewedContent = section.reviewedContent || {};

    const group = { subdomain: mapping.group };

    const processInstructionText = (text: string): string => {
      const rendered = renderTemplate(text, reviewedContent, group);

      return replaceLocationPlaceholder(rendered, authLocation);
    };

    const isHeartplaceNonMrr = mapping.group === ConfigSubdomain.HEARTPLACE;

    const standardFields: InstructionField[] = [
      ...(!isHeartplaceNonMrr
        ? [{ label: "Workflow Type", value: renderTemplate(mapping.workflowType, reviewedContent, group) }]
        : []),
      { label: "Priority", value: renderTemplate(mapping.priority, reviewedContent, group) },
      { label: "Document type", value: mapping.displayName },
      { label: "Document label", value: renderTemplate(mapping.documentLabel, reviewedContent, group) },
      { label: "Input date", value: mapping.inputDate },
      {
        label: isHeartplaceNonMrr ? "Folder" : "Upload to folder",
        value: renderTemplate(mapping.folder || "", reviewedContent, group),
      },
      {
        label: "Assign to",
        value:
          resolvedAssignment ??
          (authLocation && isPassthroughAssignee(authLocation)
            ? authLocation
            : replaceLocationPlaceholder(
                mapping.assignedTo?.replace(/\{\{?authPrefix\}\}?/g, locationPrefix ?? "HP") ?? null,
                authLocation
              )),
      },
    ].filter((field) => field.value);

    if (mapping.markAsReviewed != null) {
      standardFields.push({ label: "Mark as reviewed", value: mapping.markAsReviewed ? "Yes" : "No" });
    }

    const extraFields: InstructionField[] = [...getGroupExtraFields(mapping.group, task, section)];

    if (staffLocation) {
      const staffLocationValue =
        isHeartplaceNonMrr && locationPrefix ? `${locationPrefix} ${staffLocation}` : staffLocation;

      extraFields.push({ label: isHeartplaceNonMrr ? "Fax sent to" : "Staff Location", value: staffLocationValue });
    }

    if (mapping.teMessage) {
      extraFields.push({ label: "TE message", value: mapping.teMessage });
    }

    return {
      displayName: mapping.displayName,
      standardFields,
      extraFields,
      instructions: mapping.instructions,
      note: mapping.note ?? null,
      noteLabel: mapping.noteLabel ?? null,
      statusLine: mapping.statusLine ?? null,
      workflowAction: mapping.workflowAction ?? null,
      additionalWorkflow: mapping.additionalWorkflow,
      faxRecipientNumber,
      tags: mapping.tags ?? null,
      stepOutputs: stepOutputs ?? null,
      ccDescription: ccDescription ?? null,
      processInstructionText,
    };
  }, [
    mapping,
    stepOutputs,
    ccDescription,
    section.reviewedContent,
    authLocation,
    staffLocation,
    locationPrefix,
    faxRecipientNumber,
    resolvedAssignment,
    task.patient?.familyName,
    task.patient?.givenName,
    task.patient ? tryGetEhrSystemId(task.patient) : null,
    section.ehrDocumentId,
    mapping?.teMessage,
  ]);
}

export function useProcessedInstructions(task: Task, section: Section, enabled = true) {
  const {
    instructions,
    stepOutputs,
    ccDescription,
    isLoading: docLoading,
    isError: docError,
  } = useDocumentManagementInstructions(task.id, { enabled });

  const {
    authLocation,
    staffLocation,
    faxRecipientNumber,
    locationPrefix,
    resolvedAssignment,
    isLoading: locLoading,
  } = useFaxLocationAssignment(task.id, { enabled });

  const data = useBuildInstructions(task, section, instructions, stepOutputs, ccDescription, {
    authLocation,
    staffLocation,
    locationPrefix,
    faxRecipientNumber,
    resolvedAssignment,
  });

  return {
    isLoading: docLoading || locLoading,
    isError: docError,
    data,
  };
}
